import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { api } from '../../services/api';

interface Task {
  type: string;
  title: string;
  description: string;
  link: string;
  priority: 'high' | 'medium' | 'low';
}

interface Activity {
  id: string;
  action_type: string;
  entity_type: string;
  entity_name: string;
  created_at: string;
}

interface DashboardData {
  pending_applications: number;
  applications_waiting_days: number;
  bills_this_month: number;
  bills_amount_this_month: number;
  payroll_due_date: string | null;
  recent_activity: Activity[];
  tasks_needing_attention: Task[];
}

const AlertIcon = () => (
  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
  </svg>
);

const CheckCircleIcon = () => (
  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
  </svg>
);

const ArrowRightIcon = () => (
  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
  </svg>
);

function formatTime(dateString: string) {
  const date = new Date(dateString);
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const dateDay = new Date(date.getFullYear(), date.getMonth(), date.getDate());

  if (dateDay.getTime() === today.getTime()) {
    return date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
  } else {
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  }
}

function formatActionType(actionType: string): string {
  const map: Record<string, string> = {
    'create': 'Created',
    'update': 'Updated',
    'approve': 'Approved',
    'reject': 'Rejected',
    'request_info': 'Requested info for',
  };
  return map[actionType] || actionType;
}

function formatEntityType(entityType: string): string {
  const map: Record<string, string> = {
    'dealer_application': 'dealer application',
    'bill': 'bill',
    'employee_tax_info': 'employee tax info',
  };
  return map[entityType] || entityType;
}

export default function AccountingDashboard() {
  const { user } = useAuth();
  const [data, setData] = useState<DashboardData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchDashboard = async () => {
      try {
        setLoading(true);
        const result = await api.accounting.getDashboard();
        setData(result);
      } catch (err: any) {
        setError(err.message || 'Failed to load dashboard');
      } finally {
        setLoading(false);
      }
    };

    fetchDashboard();
  }, []);

  const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return 'Good morning';
    if (hour < 17) return 'Good afternoon';
    return 'Good evening';
  };

  const firstName = user?.name?.split(' ')[0] || 'there';

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700">
        {error}
      </div>
    );
  }

  const tasks = data?.tasks_needing_attention || [];
  const activities = data?.recent_activity || [];

  return (
    <div className="max-w-4xl mx-auto">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-gray-900">
          {getGreeting()}, {firstName}
        </h1>
        <p className="text-gray-500 mt-1">
          {new Date().toLocaleDateString('en-US', {
            weekday: 'long',
            year: 'numeric',
            month: 'long',
            day: 'numeric',
          })}
        </p>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <p className="text-sm text-gray-500">Pending Applications</p>
          <p className="text-2xl font-bold text-gray-900">{data?.pending_applications || 0}</p>
          {(data?.applications_waiting_days || 0) > 0 && (
            <p className="text-xs text-orange-600 mt-1">
              Oldest: {data?.applications_waiting_days} days
            </p>
          )}
        </div>
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <p className="text-sm text-gray-500">Bills This Month</p>
          <p className="text-2xl font-bold text-gray-900">{data?.bills_this_month || 0}</p>
          <p className="text-xs text-gray-500 mt-1">
            ${(data?.bills_amount_this_month || 0).toLocaleString()}
          </p>
        </div>
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <p className="text-sm text-gray-500">Quick Actions</p>
          <div className="flex gap-2 mt-2">
            <Link
              to="/accounting/expenses/bills/new"
              className="text-xs bg-blue-600 text-white px-3 py-1.5 rounded-lg hover:bg-blue-700"
            >
              Enter Bill
            </Link>
            <Link
              to="/accounting/approvals/dealers"
              className="text-xs bg-gray-100 text-gray-700 px-3 py-1.5 rounded-lg hover:bg-gray-200"
            >
              Review Apps
            </Link>
          </div>
        </div>
      </div>

      {/* Needs Attention */}
      {tasks.length > 0 && (
        <div className="mb-8">
          <h2 className="text-sm font-semibold text-red-600 uppercase tracking-wide mb-3 flex items-center gap-2">
            <AlertIcon />
            Needs Attention
          </h2>
          <div className="bg-white rounded-lg border border-gray-200 divide-y divide-gray-100">
            {tasks.map((task, index) => (
              <Link
                key={index}
                to={task.link}
                className="flex items-center justify-between p-4 hover:bg-gray-50 transition-colors"
              >
                <div>
                  <p className="font-medium text-gray-900">{task.title}</p>
                  <p className="text-sm text-gray-500">{task.description}</p>
                </div>
                <div className="flex items-center gap-2">
                  <span
                    className={`px-2 py-1 text-xs font-medium rounded-full ${
                      task.priority === 'high'
                        ? 'bg-red-100 text-red-700'
                        : task.priority === 'medium'
                        ? 'bg-yellow-100 text-yellow-700'
                        : 'bg-gray-100 text-gray-700'
                    }`}
                  >
                    {task.priority}
                  </span>
                  <ArrowRightIcon />
                </div>
              </Link>
            ))}
          </div>
        </div>
      )}

      {/* No tasks message */}
      {tasks.length === 0 && (
        <div className="mb-8 bg-green-50 border border-green-200 rounded-lg p-6 text-center">
          <CheckCircleIcon />
          <p className="text-green-700 font-medium mt-2">All caught up!</p>
          <p className="text-green-600 text-sm">No urgent tasks right now.</p>
        </div>
      )}

      {/* Done Today */}
      {activities.length > 0 && (
        <div>
          <h2 className="text-sm font-semibold text-green-600 uppercase tracking-wide mb-3 flex items-center gap-2">
            <CheckCircleIcon />
            Done Today
          </h2>
          <div className="bg-white rounded-lg border border-gray-200 divide-y divide-gray-100">
            {activities.slice(0, 5).map((activity) => (
              <div key={activity.id} className="flex items-center gap-3 p-4">
                <div className="w-6 h-6 bg-green-100 rounded-full flex items-center justify-center">
                  <svg className="w-3 h-3 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                  </svg>
                </div>
                <div className="flex-1">
                  <p className="text-sm text-gray-900">
                    {formatActionType(activity.action_type)}{' '}
                    <span className="font-medium">{activity.entity_name || formatEntityType(activity.entity_type)}</span>
                  </p>
                </div>
                <p className="text-xs text-gray-500">{formatTime(activity.created_at)}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* No activity message */}
      {activities.length === 0 && (
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-6 text-center">
          <p className="text-gray-500">No activity yet today.</p>
        </div>
      )}
    </div>
  );
}
